import { useState, useEffect, type FC } from 'react';
import {
  Megaphone,
  Plus,
  Search,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Clock,
  Trash2,
  Edit2,
  XCircle,
  ChevronLeft,
  ChevronRight,
  ShieldCheck,
  Mail,
} from 'lucide-react';
import type {
  CampaignListResponse,
  CampaignResponse,
  CampaignStatus,
  CampaignValidationResult,
  MailboxResponse,
} from '@antigravity/types';
import { apiClient, ApiClientError } from '../services/api-client';
import { CampaignModal } from '../components/campaigns/CampaignModal';
import { CampaignValidationModal } from '../components/campaigns/CampaignValidationModal';
import { ConnectSenderModal } from '../components/campaigns/ConnectSenderModal';

export const CampaignsPage: FC = () => {
  const [campaigns, setCampaigns] = useState<CampaignResponse[]>([]);
  const [mailboxes, setMailboxes] = useState<MailboxResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modals state
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] =
    useState<CampaignResponse | null>(null);

  const [isConnectSenderOpen, setIsConnectSenderOpen] = useState(false);

  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);
  const [validationResult, setValidationResult] =
    useState<CampaignValidationResult | null>(null);
  const [validatingCampaignName, setValidatingCampaignName] = useState('');

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchMailboxes = async () => {
    try {
      const res = await apiClient.listMailboxes({ limit: 50 });
      setMailboxes(res.items.filter((mb) => mb.status === 'ACTIVE'));
    } catch (err) {
      console.error('Failed to load active mailboxes', err);
    }
  };

  // Fetch mailboxes for creation dropdown
  useEffect(() => {
    fetchMailboxes();
  }, []);

  const fetchCampaigns = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res: CampaignListResponse = await apiClient.listCampaigns({
        page,
        limit: 10,
        status: statusFilter !== 'ALL' ? statusFilter : undefined,
        search: debouncedSearch.trim() || undefined,
      });
      setCampaigns(res.items);
      setTotal(res.total);
      setTotalPages(res.totalPages);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message);
      } else {
        setError('Failed to fetch campaigns. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, [page, statusFilter, debouncedSearch]);

  const handleValidate = async (campaign: CampaignResponse) => {
    setError(null);
    try {
      setValidatingCampaignName(campaign.name);
      const result = await apiClient.validateCampaign(campaign.id);
      setValidationResult(result);
      setIsValidationModalOpen(true);
      fetchCampaigns();
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message);
      } else {
        setError('Failed to run campaign validation check.');
      }
    }
  };

  const handleTransitionStatus = async (
    campaignId: string,
    targetStatus: CampaignStatus,
  ) => {
    setError(null);
    try {
      await apiClient.transitionCampaignStatus(campaignId, {
        status: targetStatus,
      });
      fetchCampaigns();
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message);
      } else {
        setError('Failed to update campaign status.');
      }
    }
  };

  const handleDelete = async (campaign: CampaignResponse) => {
    if (!window.confirm(`Are you sure you want to delete "${campaign.name}"?`)) {
      return;
    }
    setError(null);
    try {
      await apiClient.deleteCampaign(campaign.id);
      fetchCampaigns();
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message);
      } else {
        setError('Failed to delete campaign.');
      }
    }
  };

  const getStatusBadge = (status: CampaignStatus) => {
    switch (status) {
      case 'DRAFT':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-slate-800 text-slate-300 border border-slate-700">
            <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
            Draft
          </span>
        );
      case 'READY':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
            Ready
          </span>
        );
      case 'SCHEDULED':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-purple-500/10 text-purple-400 border border-purple-500/20">
            <Clock className="w-3 h-3 text-purple-400" />
            Scheduled
          </span>
        );
      case 'PROCESSING':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
            Processing
          </span>
        );
      case 'COMPLETED':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
            <CheckCircle2 className="w-3 h-3 text-emerald-400" />
            Completed
          </span>
        );
      case 'CANCELLED':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-slate-800/80 text-slate-400 border border-slate-700/60">
            <XCircle className="w-3 h-3 text-slate-400" />
            Cancelled
          </span>
        );
      case 'FAILED':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-rose-500/10 text-rose-400 border border-rose-500/20">
            <AlertCircle className="w-3 h-3 text-rose-400" />
            Failed
          </span>
        );
      default:
        return <span className="text-xs text-slate-400">{status}</span>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-slate-800">
        <div>
          <h1 className="text-2xl font-bold text-slate-100 flex items-center gap-2.5">
            <Megaphone className="w-7 h-7 text-brand-400" />
            Email Campaigns
          </h1>
          <p className="text-xs text-slate-400 mt-1">
            Tenant-isolated campaign lifecycle, draft management, and pre-flight validation.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setIsConnectSenderOpen(true)}
            className="px-3.5 py-2 text-xs font-semibold bg-slate-800 hover:bg-slate-700 text-brand-300 border border-brand-500/30 rounded-xl shadow transition-all flex items-center gap-2"
          >
            <Mail className="w-4 h-4 text-brand-400" />
            Connect Existing Email (3 Ways)
          </button>
          <button
            type="button"
            onClick={() => {
              setEditingCampaign(null);
              setIsFormModalOpen(true);
            }}
            className="px-4 py-2 text-xs font-semibold bg-brand-600 hover:bg-brand-500 text-white rounded-xl shadow-lg shadow-brand-500/20 transition-all flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            New Campaign
          </button>
        </div>
      </div>

      {/* 3-Ways Connection Helper Banner */}
      <div className="p-4 rounded-2xl bg-gradient-to-r from-brand-950/50 via-slate-900/80 to-purple-950/40 border border-brand-500/30 shadow-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-start gap-3.5">
          <div className="w-10 h-10 rounded-xl bg-brand-500/10 border border-brand-500/20 flex items-center justify-center text-brand-400 flex-shrink-0 mt-0.5">
            <Mail className="w-5 h-5" />
          </div>
          <div>
            <div className="text-xs font-bold text-white flex flex-wrap items-center gap-2">
              <span>Connect Existing Email (e.g. vijaydirector@umshiv.com)</span>
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-brand-500/20 text-brand-300 border border-brand-500/30">
                3 Flexible Ways Available
              </span>
            </div>
            <p className="text-[11px] text-slate-400 mt-1">
              Choose from <strong className="text-emerald-400">Way 1: Instant Plug & Play</strong> (Zero DNS, pre-warmed pool), <strong className="text-blue-400">Way 2: Subdomain Outreach</strong> (High volume safe), or <strong className="text-purple-400">Way 3: Direct Domain / SMTP Relay</strong>.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setIsConnectSenderOpen(true)}
          className="px-4 py-2 text-xs font-semibold bg-brand-600 hover:bg-brand-500 text-white rounded-xl shadow-md shadow-brand-500/20 transition whitespace-nowrap flex items-center gap-1.5"
        >
          <Mail className="w-3.5 h-3.5" />
          <span>Choose Connection Way</span>
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="p-3.5 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-200 text-xs flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
          <button
            type="button"
            onClick={() => setError(null)}
            className="text-xs text-rose-300 hover:text-rose-100"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Filters Bar */}
      <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between bg-slate-900/60 p-3 rounded-2xl border border-slate-800">
        <div className="flex flex-wrap items-center gap-2">
          {['ALL', 'DRAFT', 'READY', 'SCHEDULED', 'PROCESSING', 'COMPLETED', 'CANCELLED'].map(
            (st) => (
              <button
                key={st}
                type="button"
                onClick={() => {
                  setStatusFilter(st);
                  setPage(1);
                }}
                className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-colors ${
                  statusFilter === st
                    ? 'bg-brand-600/20 text-brand-300 border border-brand-500/30'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/40'
                }`}
              >
                {st.charAt(0) + st.slice(1).toLowerCase()}
              </button>
            ),
          )}
        </div>

        <div className="flex items-center gap-2">
          <div className="relative flex-1 md:w-64">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search campaigns..."
              className="w-full pl-9 pr-3 py-1.5 bg-slate-950 border border-slate-800 rounded-xl text-slate-100 text-xs focus:outline-none focus:border-brand-500"
            />
          </div>

          <button
            type="button"
            onClick={fetchCampaigns}
            title="Refresh"
            className="p-2 text-slate-400 hover:text-slate-200 bg-slate-950 border border-slate-800 rounded-xl hover:bg-slate-800 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Campaigns Table */}
      <div className="rounded-2xl border border-slate-800 bg-slate-900/40 overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-900/80 text-slate-400 uppercase tracking-wider border-b border-slate-800 text-[11px]">
              <tr>
                <th className="py-3 px-4 font-semibold">Campaign & Subject</th>
                <th className="py-3 px-4 font-semibold">Sender Mailbox</th>
                <th className="py-3 px-4 font-semibold">Recipients</th>
                <th className="py-3 px-4 font-semibold">Status</th>
                <th className="py-3 px-4 font-semibold">Scheduled / Created</th>
                <th className="py-3 px-4 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {isLoading && campaigns.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-slate-400">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-brand-400" />
                    Loading campaigns...
                  </td>
                </tr>
              ) : campaigns.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-slate-400">
                    <Megaphone className="w-8 h-8 mx-auto mb-2 text-slate-600" />
                    <p className="font-medium text-slate-300">No campaigns found</p>
                    <p className="text-xs mt-1 text-slate-400">
                      Create your first draft campaign to get started with outreach sequencing.
                    </p>
                  </td>
                </tr>
              ) : (
                campaigns.map((c) => (
                  <tr
                    key={c.id}
                    className="hover:bg-slate-800/20 transition-colors group"
                  >
                    <td className="py-3.5 px-4">
                      <div className="font-semibold text-slate-200">
                        {c.name}
                      </div>
                      <div className="text-[11px] text-slate-400 truncate max-w-xs mt-0.5">
                        {c.subject}
                      </div>
                    </td>

                    <td className="py-3.5 px-4 font-mono text-[11px] text-slate-300">
                      {c.fromMailbox?.emailAddress || c.fromMailboxId}
                    </td>

                    <td className="py-3.5 px-4">
                      <span className="font-semibold text-slate-200">
                        {c.totalRecipients}
                      </span>{' '}
                      <span className="text-[11px] text-slate-400">recipients</span>
                    </td>

                    <td className="py-3.5 px-4">
                      {getStatusBadge(c.status)}
                    </td>

                    <td className="py-3.5 px-4 text-[11px] text-slate-400">
                      {c.scheduledAt ? (
                        <div className="flex items-center gap-1 text-purple-300 font-medium">
                          <Clock className="w-3.5 h-3.5" />
                          {new Date(c.scheduledAt).toLocaleString()}
                        </div>
                      ) : (
                        <div>{new Date(c.createdAt).toLocaleDateString()}</div>
                      )}
                    </td>

                    <td className="py-3.5 px-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {/* Validate Button */}
                        <button
                          type="button"
                          onClick={() => handleValidate(c)}
                          title="Check Readiness"
                          className="p-1.5 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded-lg transition-colors"
                        >
                          <ShieldCheck className="w-4 h-4" />
                        </button>

                        {/* State Transition Actions */}
                        {c.status === 'DRAFT' && (
                          <button
                            type="button"
                            onClick={() => handleTransitionStatus(c.id, 'READY')}
                            title="Mark as Ready"
                            className="p-1.5 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 rounded-lg transition-colors"
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </button>
                        )}

                        {c.status === 'READY' && (
                          <button
                            type="button"
                            onClick={() => handleTransitionStatus(c.id, 'DRAFT')}
                            title="Revert to Draft"
                            className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-lg transition-colors text-[11px]"
                          >
                            Unready
                          </button>
                        )}

                        {(c.status === 'READY' || c.status === 'SCHEDULED') && (
                          <button
                            type="button"
                            onClick={() => handleTransitionStatus(c.id, 'CANCELLED')}
                            title="Cancel Campaign"
                            className="p-1.5 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 rounded-lg transition-colors"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        )}

                        {/* Edit Button */}
                        {(c.status === 'DRAFT' || c.status === 'READY') && (
                          <button
                            type="button"
                            onClick={() => {
                              setEditingCampaign(c);
                              setIsFormModalOpen(true);
                            }}
                            title="Edit Campaign"
                            className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-lg transition-colors"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                        )}

                        {/* Delete Button */}
                        {(c.status === 'DRAFT' ||
                          c.status === 'CANCELLED' ||
                          c.status === 'FAILED') && (
                          <button
                            type="button"
                            onClick={() => handleDelete(c)}
                            title="Delete Campaign"
                            className="p-1.5 text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {total > 0 && (
          <div className="py-3 px-4 border-t border-slate-800 bg-slate-900/60 flex items-center justify-between text-xs text-slate-400">
            <div>
              Showing {campaigns.length} of {total} campaign{total === 1 ? '' : 's'}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                disabled={page <= 1}
                className="p-1.5 rounded-lg border border-slate-800 hover:bg-slate-800 disabled:opacity-40 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-slate-300 font-medium">
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                disabled={page >= totalPages}
                className="p-1.5 rounded-lg border border-slate-800 hover:bg-slate-800 disabled:opacity-40 transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Campaign Form Modal */}
      <CampaignModal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        onSuccess={() => fetchCampaigns()}
        campaign={editingCampaign}
        mailboxes={mailboxes}
        onOpenConnectSender={() => setIsConnectSenderOpen(true)}
      />

      {/* Validation Checklist Modal */}
      <CampaignValidationModal
        isOpen={isValidationModalOpen}
        onClose={() => setIsValidationModalOpen(false)}
        result={validationResult}
        campaignName={validatingCampaignName}
      />

      {/* Connect Sender (3 Ways) Modal */}
      <ConnectSenderModal
        isOpen={isConnectSenderOpen}
        onClose={() => setIsConnectSenderOpen(false)}
        onSuccess={(newMb) => {
          setMailboxes((prev) => [newMb, ...prev]);
          fetchMailboxes();
        }}
      />
    </div>
  );
};
export default CampaignsPage;
